import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../shared/api/Api";
import { showToast } from "../../utils/utils";

function PlacementTestCardSkeleton() {
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white p-5 sm:p-6 shadow-sm animate-pulse">
      <div className="flex gap-2 mb-4">
        <div className="h-6 w-24 rounded-full bg-gray-200" />
        <div className="h-6 w-16 rounded-full bg-gray-200" />
      </div>
      <div className="h-6 w-full max-w-md rounded bg-gray-200 mb-3" />
      <div className="h-3 w-full rounded bg-gray-100 mb-2" />
      <div className="h-3 w-full max-w-[92%] rounded bg-gray-100 mb-2" />
      <div className="h-3 w-full max-w-[55%] rounded bg-gray-100 mt-6" />
      <div className="h-10 w-32 rounded-lg bg-gray-200 mt-6" />
    </div>
  );
}

const PublicPlacementTestsPage = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTests = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(API.PlacementTests.all, {
          params: { status: "published" },
        });
        if (data.status === 200) {
          const payload = data?.data;
          const items =
            (Array.isArray(payload) && payload) ||
            (Array.isArray(payload?.data) && payload.data) ||
            (Array.isArray(payload?.placement_tests) && payload.placement_tests) ||
            [];
          setTests(items);
        }
      } catch (error) {
        showToast("Error", "Failed to load placement tests.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchTests();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50/80">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-14">
        {/* Hero */}
        <header className="text-center sm:text-left mb-10 sm:mb-12">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary/90 mb-2">
            Language Village
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
            Placement tests
          </h1>
          <p className="mt-3 text-base sm:text-lg text-gray-600 max-w-2xl sm:max-w-none mx-auto sm:mx-0 leading-relaxed">
            Find the right assessment for your level. Choose a test below to see details, fees, and how to get
            started.
          </p>
        </header>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((k) => (
              <PlacementTestCardSkeleton key={k} />
            ))}
          </div>
        ) : tests.length === 0 ? (
          <div
            role="status"
            className="rounded-2xl border border-dashed border-gray-300 bg-white/80 px-6 py-14 sm:py-16 text-center max-w-lg mx-auto shadow-sm"
          >
            <span className="text-4xl sm:text-5xl" aria-hidden>
              📋
            </span>
            <h2 className="mt-4 text-lg sm:text-xl font-semibold text-gray-900">No tests available yet</h2>
            <p className="mt-2 text-sm sm:text-base text-gray-600 leading-relaxed">
              There are no published placement tests right now. Check back soon or contact the institute.
            </p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="mt-8 inline-flex items-center justify-center rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              Back to home
            </button>
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6 list-none p-0 m-0">
            {tests.map((test) => {
              const price = Number(test.price ?? 0);
              const isFree = price <= 0;
              const isPre = test.type === "pre_enrollment";

              return (
                <li key={test.id}>
                  <article className="group h-full flex flex-col rounded-2xl border border-gray-200/90 bg-white p-5 sm:p-6 shadow-sm transition duration-200 hover:border-primary/25 hover:shadow-md hover:-translate-y-0.5">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="inline-flex items-center rounded-full border border-blue-200/80 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-800">
                        {isPre ? "Course placement" : "Standalone"}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          isFree
                            ? "border-emerald-200/80 bg-emerald-50 text-emerald-800"
                            : "border-amber-200/80 bg-amber-50 text-amber-900"
                        }`}
                      >
                        {isFree ? "Free" : `$${price.toFixed(2)}`}
                      </span>
                    </div>

                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-snug group-hover:text-primary transition-colors">
                      {test.title}
                    </h2>

                    {test.description ? (
                      <p className="mt-2 text-sm text-gray-600 line-clamp-3 flex-grow leading-relaxed">
                        {test.description}
                      </p>
                    ) : (
                      <p className="mt-2 text-sm text-gray-400 italic flex-grow">No description provided.</p>
                    )}

                    <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm text-gray-500 border-t border-gray-100 pt-4">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-gray-400" aria-hidden>
                          ⏱
                        </span>
                        {test.duration_minutes} min
                      </span>
                      {test.course?.title && (
                        <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
                          <span className="text-gray-400 shrink-0" aria-hidden>
                            📚
                          </span>
                          <span className="truncate" title={test.course.title}>
                            {test.course.title}
                          </span>
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => navigate(`/placement-tests/${test.id}`)}
                      className="mt-6 w-full sm:w-auto sm:self-start inline-flex items-center justify-center rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                      View details
                      <span className="ml-1.5 transition group-hover:translate-x-0.5" aria-hidden>
                        →
                      </span>
                    </button>
                  </article>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PublicPlacementTestsPage;
